import React, { useEffect, useMemo } from 'react';

import { FormDialog } from '@components/admin/FormDialog';
import { TextInput } from '@components/admin/Forms';
import { LogForm } from '@components/Form';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { SpinnerModal } from '@components/SpinnerModal';
import { agencyStrings } from '@data/agency';
import { routePlanStrings } from '@data/routePlan';
import { usePatchCompany } from '@hooks/mutation/dashboard/usePatchCompany';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import {
  areMemberAgencyFormItemAgencyOptionsEqual,
  memberAgencyForm,
  MemberAgencyForm,
  MemberAgencyFormItem,
} from '@models/RoutePlan';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createValidate, joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import { Form, Formik, useField } from 'formik';
import { groupBy, sortBy, uniq } from 'lodash-es';
import { unwrap } from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: `${agencyStrings.agency}を変更する`,
    selectAgencyPrompt: `${agencyStrings.agency}を指定してください`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  memberAgencyForm: {
    width: 960,
    display: 'grid',
    gridTemplateColumns:
      'minmax(max-content, 0.5fr) minmax(max-content, 1fr) minmax(max-content, 1fr) 3fr',
    '& *[role="head"]': {
      ...common.fontWeight600,
      ...common.textGray800,
      padding: '20px 30px',
    },
    '& *[role="data"]': {
      ...common.middleText,
      ...common.textGray800,
      ...common.flexColumn,
      ...common.justifyCenter,
      padding: '16px 30px',
    },
  },
  memberAgencyFormBody: {
    paddingTop: 8,
  },
  divider: {
    gridColumn: 'span 4',
    height: 1,
    backgroundColor: theme.palette.base[300],
  },
}));

const MemberAgencyItem: React.FC<{ index: number; name: string; disabled?: boolean }> = ({
  index,
  name,
  disabled,
}) => {
  const [field, , helpers] = useField<MemberAgencyFormItem>(joinKeyPath(name, index));

  const [itemsField, , itemsHelpers] = useField<MemberAgencyFormItem[]>(name);

  const classes = { ...useCommonStyles(), ...useStyles() };
  const { value } = field;

  const selectedPlan = value.options.find((p) => p.id === value.route_plan_id);

  const routePlanName = value.options[0].name;

  const canSelect = value.options.length > 1;

  useEffect(() => {
    helpers.setTouched(true);
    // Auto fill items of the same choices

    const currentAgencyId = value.options.find((a) => a.id === value.route_plan_id)?.agency.id;

    if (currentAgencyId) {
      itemsHelpers.setValue(
        itemsField.value.map((v) =>
          v.plan_id === value.plan_id ||
          (!v.route_plan_id && areMemberAgencyFormItemAgencyOptionsEqual(v, value))
            ? {
                ...v,
                route_plan_id: v.options.find((o) => o.agency.id === currentAgencyId)!.id,
              }
            : v,
        ),
      );
    }
  }, [field.value.route_plan_id]);

  const serviceNames = uniq(value.options.map((o) => o.service_name));

  return (
    <>
      <div className={classes.divider} />
      <div style={{ display: 'contents' }}>
        <div role="data">{index + 1}</div>

        <div role="data">{serviceNames.join('/')}</div>
        <div role="data">{routePlanName}</div>
        <div role="data" style={{ position: 'relative', paddingTop: 10, paddingBottom: 10 }}>
          {canSelect ? (
            <TextInput
              name={joinKeyPath(name, index, 'route_plan_id')}
              noLabel
              choose
              options={field.value.options.map((o) => ({
                label: o.agency.name_en_short,
                value: o.id,
              }))}
              noErrorMessage
              disabled={disabled}
            />
          ) : (
            selectedPlan?.agency.name_en_short
          )}
        </div>
      </div>
    </>
  );
};

export const MemberAgencyDialog: React.FC<{
  id: number;
  open: boolean;
  onClose: () => void;
  disabled?: boolean;
}> = (props) => {
  const { id, open, onClose, disabled = false } = props;

  const classes = useStyles();

  const routePlanList = useRoutePlanList({}, { refetchOnWindowFocus: false, enabled: open });
  const companyRoutePlanList = useRoutePlanList(
    {
      company_id: id,
    },
    { refetchOnWindowFocus: false, enabled: open },
  );

  const patchCompany = usePatchCompany(id);

  const initialValues: MemberAgencyForm = useMemo(() => {
    if (routePlanList.isSuccess && companyRoutePlanList.isSuccess) {
      const groups = groupBy(routePlanList.data, (p) => p.plan_id);
      const selectedIds = new Set(companyRoutePlanList.data.map((d) => d.id));
      return {
        items: sortBy(
          Object.values(groups).map((plans) => ({
            plan_id: plans[0].plan_id,
            options: plans,
            route_plan_id:
              plans.find((p) => selectedIds.has(p.id))?.id ??
              (plans.length === 1 ? plans[0].id : 0),
          })),
          (p) => p.plan_id,
        ),
      };
    } else {
      return {
        items: [],
      };
    }
  }, [routePlanList.data, companyRoutePlanList.data]);

  if (routePlanList.isSuccess && companyRoutePlanList.isSuccess) {
    return (
      <FormDialog
        dialogProps={{ maxWidth: false }}
        alignTitle="center"
        open={open}
        onClose={onClose}
        title={strings.title}
        classes={{
          body: classes.memberAgencyFormBody,
        }}
      >
        <Formik<MemberAgencyForm>
          initialValues={initialValues}
          validate={createValidate(memberAgencyForm)}
          onSubmit={async (_values) => {
            if (!disabled) {
              const values = unwrap(memberAgencyForm, _values);

              await patchCompany.mutateAsync(
                {
                  route_plans: values.items.map((item) => item.route_plan_id),
                },
                {
                  onSuccess: companyRoutePlanList.refetch,
                },
              );
            }
          }}
        >
          <Form className={classes.memberAgencyForm}>
            <LogForm name="member-agency" />
            <SubmittingOverlay />

            <div role="head">NO</div>
            <div role="head">{routePlanStrings.serviceName}</div>
            <div role="head">{routePlanStrings.routeName}</div>
            <div role="head">{agencyStrings.agency}</div>

            {initialValues.items.map((v, i) => (
              <MemberAgencyItem key={v.plan_id} index={i} name="items" disabled={disabled} />
            ))}
          </Form>
        </Formik>
      </FormDialog>
    );
  } else {
    return open ? <SpinnerModal open /> : null;
  }
};
